import type { AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Axios rejects with a response object rather than an Error, and throwing that
 * response straight through left every downstream catch holding a value with no
 * name, no stack and no prototype chain. PostHog can only record such a value as
 * a "Non-Error promise rejection", which collapses unrelated throw sites into one
 * issue.
 *
 * `status`, `data`, `response` and `config` stay at the top level because callers
 * already read them there, and `extractErrorMessage` looks for `data.detail`
 * before anything else.
 *
 * `message` is axios's technical string ("Request failed with status code 500").
 * It is for error tracking, never for a user. `extractErrorMessage` knows that
 * and falls back to its own copy instead.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly data: unknown;
  readonly response?: AxiosResponse;
  readonly config?: AxiosRequestConfig;

  constructor(
    message: string,
    options: {
      status: number;
      data?: unknown;
      response?: AxiosResponse;
      config?: AxiosRequestConfig;
      cause?: unknown;
    },
  ) {
    super(message, { cause: options.cause });
    this.name = 'ApiRequestError';
    this.status = options.status;
    this.data = options.data;
    this.response = options.response;
    this.config = options.config;
  }
}

/**
 * Statuses the product models as ordinary outcomes: a signed-out visitor, a
 * forbidden action, a deleted job, a business rule refusing. Reporting these as
 * exceptions made them 28% of the error stream and none of them was ever a defect.
 *
 * 422 is deliberately absent, see below.
 */
const EXPECTED_STATUSES = new Set([400, 401, 403, 404, 409]);

/**
 * FastAPI's own `RequestValidationError` renders `{"detail": [{loc, type}, ...]}`,
 * and means the client sent a shape the server never declared. That is a defect.
 * The app's `ValidationError` renders an RFC 7807 body for the same status and
 * only means someone typed something invalid.
 *
 * The array is what separates them, and it matters because most of
 * `fetchWithCsrf` points at `/asyncapi/v2/*`, so both land here.
 */
function isRequestShapeRejection(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  return Array.isArray((data as { detail?: unknown }).detail);
}

export function isUnexpectedApiFailure(status: number, data?: unknown): boolean {
  if (status === 422) return isRequestShapeRejection(data);
  return !EXPECTED_STATUSES.has(status);
}
